import { Phone, Building2, Download, CheckCircle2, AlertTriangle, Wifi, Signal, TrendingUp, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { exportarLinhasMovelExcel } from '@/utils/exportLinhasLeads';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';

interface LinhaMovel {
  telefone?: string;
  plano?: string;
  fatura_media?: number;
  gb_contratado?: number;
  gb_trafegado?: number;
  aparelho?: string;
  m?: number | null;
  fidelizado?: string;
}

interface ContaFixa {
  conta?: string;
  designador?: string;
  produto?: string;
  tipo_produto?: string;
  tecnologia?: string;
  valor?: number;
  m?: number | null;
  endereco?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
}

interface TotaisSnapshot {
  qtd_linhas_movel?: number;
  qtd_contas_fixo?: number;
  total_fatura_movel?: number;
  total_fatura_fixo?: number;
  total_gb_contratado?: number;
  total_gb_trafegado?: number;
}

const formatarMoeda = (valor: number | undefined | null): string => {
  if (valor === null || valor === undefined) return 'N/A';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatarTelefone = (telefone: string | number | undefined): string => {
  if (!telefone) return '-';
  const digits = String(telefone).replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(telefone);
};

const calcularPercentualUso = (trafegado: number, contratado: number): number => {
  if (!contratado || contratado === 0) return 0;
  return Math.round((trafegado / contratado) * 100);
};

interface LeadBaseLinhasTabProps {
  lead: LeadCarteira;
}

export function LeadBaseLinhasTab({ lead }: LeadBaseLinhasTabProps) {
  const dados = lead.dados_master_lead as Record<string, unknown> | null;
  const linhasMovel = (dados?.base_linhas_movel as LinhaMovel[]) || [];
  const contasFixo = (dados?.base_linhas_fixo as ContaFixa[]) || [];
  const totaisSnapshot = (dados?.totais as TotaisSnapshot) || {};

  // Calcular totais (usar snapshot se disponível, senão calcular)
  const totalFaturaMovel = totaisSnapshot.total_fatura_movel ?? 
    linhasMovel.reduce((acc, l) => acc + (l.fatura_media || 0), 0);
  const totalFaturaFixo = totaisSnapshot.total_fatura_fixo ?? 
    contasFixo.reduce((acc, c) => acc + (c.valor || 0), 0);
  const totalGBContratado = totaisSnapshot.total_gb_contratado ?? 
    linhasMovel.reduce((acc, l) => acc + (l.gb_contratado || 0), 0);
  const totalGBTrafegado = totaisSnapshot.total_gb_trafegado ?? 
    linhasMovel.reduce((acc, l) => acc + (l.gb_trafegado || 0), 0);

  const percentualUsoGeral = calcularPercentualUso(totalGBTrafegado, totalGBContratado);
  
  // Verificar aptidão das linhas
  const linhasComMulta = linhasMovel.filter(l => l.m !== null && l.m !== undefined && l.m < 24).length;
  const linhasSemMulta = linhasMovel.filter(l => l.m !== null && l.m !== undefined && l.m >= 24).length;
  const todasSemMulta = linhasMovel.length > 0 && linhasComMulta === 0;
  
  // Verificar tecnologias fixas
  const temFTTH = contasFixo.some(c => c.tecnologia?.toUpperCase() === 'FTTH');
  const temMetalico = contasFixo.some(c => 
    c.tecnologia?.toUpperCase() === 'METÁLICO' || 
    c.tecnologia?.toUpperCase() === 'METALICO' ||
    c.tecnologia?.toUpperCase() === 'FTTC'
  );

  const handleExportarExcel = () => {
    exportarLinhasMovelExcel(
      linhasMovel,
      contasFixo,
      lead.cnpj,
      lead.razao_social || undefined
    );
  };

  const temDados = linhasMovel.length > 0 || contasFixo.length > 0;

  return (
    <div className="space-y-4">
      {/* Header com botão de exportar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">Base de Linhas Vivo</h3>
          {todasSemMulta && linhasMovel.length > 0 && (
            <Badge variant="default" className="bg-success text-xs gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Todas Sem Multa
            </Badge>
          )}
          {linhasComMulta > 0 && (
            <Badge variant="destructive" className="text-xs gap-1">
              <AlertTriangle className="w-3 h-3" />
              {linhasComMulta} com multa
            </Badge>
          )}
        </div>
        
        {temDados && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportarExcel}
            className="gap-2 text-xs"
          >
            <Download className="w-3.5 h-3.5" />
            Exportar Excel
          </Button>
        )}
      </div>

      {/* Cards de Resumo */}
      {temDados && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-primary/20">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-primary/10">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Linhas Móveis</p>
                  <p className="text-lg font-bold">{linhasMovel.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-success/20">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-success/10">
                  <DollarSign className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fatura Total</p>
                  <p className="text-lg font-bold">{formatarMoeda(totalFaturaMovel + totalFaturaFixo)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-info/20">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-info/10">
                  <Signal className="w-4 h-4 text-info" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">GB Contratado</p>
                  <p className="text-lg font-bold">{totalGBContratado.toFixed(0)} GB</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-warning/20">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-md bg-warning/10">
                  <TrendingUp className="w-4 h-4 text-warning" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">GB Trafegado</p>
                  <p className="text-lg font-bold">
                    {totalGBTrafegado.toFixed(0)} GB
                    <span className="text-xs font-normal text-muted-foreground ml-1">
                      ({percentualUsoGeral}%)
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Linhas Móveis */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Linhas Móveis ({linhasMovel.length})
            </div>
            {linhasMovel.length > 0 && (
              <div className="flex items-center gap-2 text-xs font-normal">
                <span className="text-success">{linhasSemMulta} sem multa</span>
                {linhasComMulta > 0 && (
                  <span className="text-danger">{linhasComMulta} com multa</span>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-3">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs w-[120px]">Telefone</TableHead>
                  <TableHead className="text-xs">Plano</TableHead>
                  <TableHead className="text-xs text-right w-[90px]">Fatura</TableHead>
                  <TableHead className="text-xs text-right w-[80px]">GB Contr.</TableHead>
                  <TableHead className="text-xs text-right w-[80px]">GB Traf.</TableHead>
                  <TableHead className="text-xs w-[130px]">Aparelho</TableHead>
                  <TableHead className="text-xs text-center w-[70px]">M</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhasMovel.length > 0 ? (
                  linhasMovel.map((linha, i) => {
                    const isSemMulta = linha.m !== null && linha.m !== undefined && linha.m >= 24;
                    return (
                      <TableRow key={i} className="text-xs">
                        <TableCell className="font-mono text-xs">{formatarTelefone(linha.telefone)}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={linha.plano}>{linha.plano || '-'}</TableCell>
                        <TableCell className="text-right">{formatarMoeda(linha.fatura_media)}</TableCell>
                        <TableCell className="text-right">{linha.gb_contratado ?? '-'}</TableCell>
                        <TableCell className="text-right">{linha.gb_trafegado ?? '-'}</TableCell>
                        <TableCell className="max-w-[130px] truncate" title={linha.aparelho}>{linha.aparelho || '-'}</TableCell>
                        <TableCell className="text-center">
                          {linha.m !== null && linha.m !== undefined ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge 
                                    variant={isSemMulta ? 'default' : 'destructive'} 
                                    className={`text-xs ${isSemMulta ? 'bg-success' : ''}`}
                                  >
                                    {linha.m}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {isSemMulta ? 'Sem multa (M ≥ 24)' : 'Com multa (M < 24)'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                      Nenhuma linha móvel importada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {linhasMovel.length > 0 && (
                <TableFooter>
                  <TableRow className="bg-muted/30 font-medium">
                    <TableCell className="text-xs">TOTAL</TableCell>
                    <TableCell className="text-xs">{linhasMovel.length} linhas</TableCell>
                    <TableCell className="text-xs text-right">{formatarMoeda(totalFaturaMovel)}</TableCell>
                    <TableCell className="text-xs text-right">{totalGBContratado.toFixed(0)}</TableCell>
                    <TableCell className="text-xs text-right">{totalGBTrafegado.toFixed(0)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>
      
      {/* Contas Fixas */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Contas Fixas ({contasFixo.length})
            </div>
            {contasFixo.length > 0 && (
              <div className="flex items-center gap-2">
                {temFTTH && (
                  <Badge variant="default" className="text-xs bg-success gap-1">
                    <Wifi className="w-3 h-3" />
                    FTTH
                  </Badge>
                )}
                {temMetalico && (
                  <Badge variant="outline" className="text-xs text-warning border-warning gap-1">
                    💡 Oportunidade Fibra
                  </Badge>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-3">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-xs w-[100px]">Conta</TableHead>
                  <TableHead className="text-xs">Produto</TableHead>
                  <TableHead className="text-xs w-[90px]">Tecnologia</TableHead>
                  <TableHead className="text-xs text-right w-[80px]">Valor</TableHead>
                  <TableHead className="text-xs w-[180px]">Endereço</TableHead>
                  <TableHead className="text-xs text-center w-[60px]">M</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contasFixo.length > 0 ? (
                  contasFixo.map((conta, i) => {
                    const isSemMulta = conta.m !== null && conta.m !== undefined && conta.m >= 24;
                    const isMetalico = conta.tecnologia?.toUpperCase() === 'METÁLICO' || 
                                       conta.tecnologia?.toUpperCase() === 'METALICO' ||
                                       conta.tecnologia?.toUpperCase() === 'FTTC';
                    const isFTTH = conta.tecnologia?.toUpperCase() === 'FTTH';
                    
                    return (
                      <TableRow key={i} className="text-xs">
                        <TableCell className="font-mono text-xs">{conta.conta || '-'}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={conta.produto}>{conta.produto || '-'}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={isFTTH ? 'default' : isMetalico ? 'outline' : 'secondary'}
                            className={`text-xs ${isFTTH ? 'bg-success' : isMetalico ? 'text-warning border-warning' : ''}`}
                          >
                            {conta.tecnologia || 'N/I'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatarMoeda(conta.valor)}</TableCell>
                        <TableCell className="max-w-[180px] truncate" title={`${conta.endereco || ''}, ${conta.bairro || ''} - ${conta.municipio || ''}/${conta.uf || ''}`}>
                          {conta.endereco ? `${conta.endereco}, ${conta.bairro || ''}` : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {conta.m !== null && conta.m !== undefined ? (
                            <Badge 
                              variant={isSemMulta ? 'default' : 'destructive'} 
                              className={`text-xs ${isSemMulta ? 'bg-success' : ''}`}
                            >
                              {conta.m}
                            </Badge>
                          ) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                      Nenhuma conta fixa importada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {contasFixo.length > 0 && (
                <TableFooter>
                  <TableRow className="bg-muted/30 font-medium">
                    <TableCell className="text-xs">TOTAL</TableCell>
                    <TableCell className="text-xs">{contasFixo.length} contas</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-xs text-right">{formatarMoeda(totalFaturaFixo)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
